import { Redirect, Stack, usePathname } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';

import { AuthenticatedHeader } from '@/src/components/AuthenticatedHeader';
import { ErrorView } from '@/src/components/ErrorView';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { SessionLoadingView } from '@/src/components/SessionLoadingView';
import { useAuth } from '@/src/features/auth/use-auth';
import { isDeletionBlocking } from '@/src/features/account-deletion/account-deletion-service';
import { hasRecordedLegalAcceptance } from '@/src/features/legal/legal-service';
import { hasCompletedOnboarding } from '@/src/features/profile/profile-service';
import { useProfile } from '@/src/features/profile/use-profile';
import { PwaFirstRunPushPrompt } from '@/src/features/pwa/PwaFirstRunPushPrompt';
import { colors } from '@/src/theme/colors';

export default function AppLayout() {
  const pathname = usePathname();
  const { authInitTimedOut, initializing, syncCurrentAuthEmail, user } =
    useAuth();
  const profileQuery = useProfile(user?.uid, {
    refetchOnMount: 'always',
  });
  const refetchProfile = profileQuery.refetch;
  const emailSyncKeyRef = useRef<string | null>(null);
  const [resumeProfileRefreshPending, setResumeProfileRefreshPending] =
    useState(false);
  const appStateRef = useRef(AppState.currentState);
  const isPublicPostPath = pathname.startsWith('/posts/');
  const isVerifyEmailPath = pathname === '/verify-email';
  const providerIds = user?.providerData.map((provider) => provider.providerId);
  const hasExternalAuthProvider =
    providerIds?.some(
      (providerId) =>
        providerId === 'facebook.com' || providerId === 'google.com',
    ) ?? false;
  const hasPasswordProvider = providerIds?.includes('password') ?? false;
  const needsExternalAuthRepair = Boolean(
    user &&
    hasExternalAuthProvider &&
    (!user.email?.trim() || !hasPasswordProvider),
  );

  useEffect(() => {
    if (!user?.uid) {
      return undefined;
    }

    let cancelled = false;

    function refetchProfileAfterResume() {
      setResumeProfileRefreshPending(true);
      void refetchProfile().finally(() => {
        if (!cancelled) {
          setResumeProfileRefreshPending(false);
        }
      });
    }

    const appStateSubscription = AppState.addEventListener(
      'change',
      (nextState) => {
        const wasBackgrounded = /inactive|background/.test(appStateRef.current);
        appStateRef.current = nextState;

        if (wasBackgrounded && nextState === 'active') {
          refetchProfileAfterResume();
        }
      },
    );

    const documentRef = typeof document === 'undefined' ? null : document;

    function handleVisibilityChange() {
      if (documentRef?.visibilityState === 'visible') {
        refetchProfileAfterResume();
      }
    }

    documentRef?.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      appStateSubscription.remove();
      documentRef?.removeEventListener(
        'visibilitychange',
        handleVisibilityChange,
      );
    };
  }, [refetchProfile, user?.uid]);

  useEffect(() => {
    const authEmail = user?.email?.trim();
    const profileEmail = profileQuery.data?.email?.trim();

    if (
      !user?.uid ||
      !user.emailVerified ||
      !authEmail ||
      !profileEmail ||
      authEmail.toLowerCase() === profileEmail.toLowerCase()
    ) {
      return;
    }

    const syncKey = `${user.uid}:${authEmail.toLowerCase()}`;

    if (emailSyncKeyRef.current === syncKey) {
      return;
    }

    emailSyncKeyRef.current = syncKey;

    void syncCurrentAuthEmail()
      .then(() => refetchProfile())
      .catch((error) => {
        console.warn('Failed to sync verified auth email.', error);
      });
  }, [
    profileQuery.data?.email,
    refetchProfile,
    syncCurrentAuthEmail,
    user?.email,
    user?.emailVerified,
    user?.uid,
  ]);

  const deletionBlocking = isDeletionBlocking(profileQuery.data?.deletion);
  const hasActivatedProfile = Boolean(profileQuery.data);
  const needsEmailVerification = Boolean(
    user && !user.emailVerified && !hasActivatedProfile,
  );
  const onboardingComplete = hasCompletedOnboarding(profileQuery.data);
  const hasLegalAcceptance = hasRecordedLegalAcceptance(
    profileQuery.data?.legal,
  );
  const staleProfileWouldBlockAccess =
    deletionBlocking ||
    needsEmailVerification ||
    !onboardingComplete ||
    needsExternalAuthRepair ||
    !hasLegalAcceptance;
  const startupProfileNeedsFreshCheck =
    Boolean(user?.uid) &&
    (!isPublicPostPath || deletionBlocking) &&
    !profileQuery.isFetchedAfterMount &&
    profileQuery.fetchStatus !== 'idle';
  const waitingForFreshBlockingProfile =
    startupProfileNeedsFreshCheck ||
    (resumeProfileRefreshPending && staleProfileWouldBlockAccess);

  if (initializing || authInitTimedOut) {
    return <SessionLoadingView />;
  }

  if (!user && !isPublicPostPath) {
    return <Redirect href="/sign-in" />;
  }

  if (!user && isPublicPostPath) {
    return (
      <Stack
        screenOptions={{
          animation: 'ios_from_right',
          animationTypeForReplace: 'push',
          contentStyle: { backgroundColor: colors.background },
          gestureDirection: 'horizontal',
          gestureEnabled: true,
          header: () => <AuthenticatedHeader />,
          presentation: 'card',
        }}
      >
        <Stack.Screen
          name="(tabs)"
          options={{ animation: 'none', headerShown: false }}
        />
        <Stack.Screen
          name="account-deletion-pending"
          options={{
            animation: 'slide_from_bottom',
            gestureDirection: 'vertical',
            headerShown: false,
            presentation: 'modal',
          }}
        />
        <Stack.Screen
          name="posts/[postId]"
          options={{
            animation: 'ios_from_right',
            presentation: 'card',
          }}
        />
      </Stack>
    );
  }

  if (profileQuery.isLoading) {
    return <LoadingView message="Checking account agreements..." />;
  }

  if (profileQuery.isError) {
    return (
      <Screen title="Unable to load account">
        <ErrorView
          message={profileQuery.error.message}
          onRetry={() => {
            void profileQuery.refetch();
          }}
        />
      </Screen>
    );
  }

  if (waitingForFreshBlockingProfile) {
    return <LoadingView message="Checking account status..." />;
  }

  if (pathname === '/account-deletion-pending' && !deletionBlocking) {
    if (needsEmailVerification) {
      return <Redirect href="/verify-email" />;
    }

    if (!onboardingComplete || needsExternalAuthRepair) {
      return <Redirect href="/onboarding" />;
    }

    if (!hasLegalAcceptance) {
      return <Redirect href="/legal-consent" />;
    }

    return <Redirect href="/home" />;
  }

  if (isVerifyEmailPath && !needsEmailVerification) {
    if (!onboardingComplete || needsExternalAuthRepair) {
      return <Redirect href="/onboarding" />;
    }

    if (!hasLegalAcceptance) {
      return <Redirect href="/legal-consent" />;
    }

    return <Redirect href="/home" />;
  }

  if (!isVerifyEmailPath && needsEmailVerification) {
    return <Redirect href="/verify-email" />;
  }

  if (pathname !== '/account-deletion-pending' && deletionBlocking) {
    return <Redirect href="/account-deletion-pending" />;
  }

  if (
    pathname !== '/onboarding' &&
    pathname !== '/verify-email' &&
    pathname !== '/account-deletion-pending' &&
    (!onboardingComplete || needsExternalAuthRepair)
  ) {
    return <Redirect href="/onboarding" />;
  }

  if (
    pathname !== '/legal-consent' &&
    pathname !== '/onboarding' &&
    pathname !== '/verify-email' &&
    !hasLegalAcceptance
  ) {
    return <Redirect href="/legal-consent" />;
  }

  const showFirstRunPushPrompt =
    !deletionBlocking &&
    !needsEmailVerification &&
    onboardingComplete &&
    hasLegalAcceptance &&
    !needsExternalAuthRepair;

  return (
    <>
      <Stack
        screenOptions={{
          animation: 'ios_from_right',
          animationTypeForReplace: 'push',
          contentStyle: { backgroundColor: colors.background },
          gestureDirection: 'horizontal',
          gestureEnabled: true,
          header: () => <AuthenticatedHeader />,
          presentation: 'card',
        }}
      >
        <Stack.Screen
          name="(tabs)"
          options={{ animation: 'none', headerShown: false }}
        />
        <Stack.Screen
          name="account-deletion-pending"
          options={{
            animation: 'slide_from_bottom',
            gestureDirection: 'vertical',
            headerShown: false,
            presentation: 'modal',
          }}
        />
        <Stack.Screen
          name="verify-email"
          options={{
            animation: 'slide_from_bottom',
            gestureDirection: 'vertical',
            headerShown: false,
            presentation: 'modal',
          }}
        />
        <Stack.Screen
          name="verify-account"
          options={{
            animation: 'ios_from_right',
            presentation: 'card',
          }}
        />
        <Stack.Screen
          name="garage-manager/[vehicleId]"
          options={{
            animation: 'ios_from_right',
            presentation: 'card',
          }}
        />
        <Stack.Screen
          name="garage-manager/create"
          options={{
            animation: 'slide_from_bottom',
            gestureDirection: 'vertical',
            presentation: 'modal',
          }}
        />
        <Stack.Screen
          name="posts/[postId]"
          options={{
            animation: 'ios_from_right',
            presentation: 'card',
          }}
        />
      </Stack>
      <PwaFirstRunPushPrompt enabled={showFirstRunPushPrompt} />
    </>
  );
}
